import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSettings, FiCheck, FiInfo } from 'react-icons/fi';

const CookieBanner = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.95), rgba(139, 92, 246, 0.1));
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  z-index: 1000;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
`;

const CookieContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const CookieText = styled.div`
  flex: 1;
  color: #e2e8f0;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const CookieLink = styled.a`
  color: #8b5cf6;
  text-decoration: underline;
  font-weight: 500;
  
  &:hover {
    color: #ec4899;
  }
`;

const CookieButtons = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const CookieButton = styled(motion.button)<{ variant?: 'primary' | 'secondary' | 'settings' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;

  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, #8b5cf6, #ec4899);
    color: white;
    box-shadow: 0 4px 16px rgba(139, 92, 246, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
    }
  `}

  ${props => props.variant === 'secondary' && `
    background: transparent;
    color: #8b5cf6;
    border: 2px solid #8b5cf6;
    
    &:hover {
      background: #8b5cf6;
      color: white;
    }
  `}

  ${props => props.variant === 'settings' && `
    background: rgba(139, 92, 246, 0.1);
    color: #8b5cf6;
    border: 1px solid rgba(139, 92, 246, 0.3);
    
    &:hover {
      background: rgba(139, 92, 246, 0.2);
    }
  `}

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const SettingsModal = styled(motion.div)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.95), rgba(139, 92, 246, 0.1));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  z-index: 1001;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const SettingsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SettingsTitle = styled.h3`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
`;

const CookieCategory = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const CategoryTitle = styled.h4`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
`;

const CategoryToggle = styled(motion.button)<{ isEnabled: boolean }>`
  width: 50px;
  height: 24px;
  background: ${props => props.isEnabled ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'rgba(255, 255, 255, 0.1)'};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${props => props.isEnabled ? 'flex-end' : 'flex-start'};
  padding: 2px;
`;

const ToggleHandle = styled(motion.div)`
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const CategoryDescription = styled.p`
  color: #94a3b8;
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0;
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

interface CookieConsentProps {
  isVisible: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ isVisible, onAccept, onDecline }) => {
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  const handleSavePreferences = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    localStorage.setItem('cookiesConsent', 'custom');
    setShowSettings(false);
    onAccept();
  };

  const handleAcceptAll = () => {
    const allEnabled = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    localStorage.setItem('cookiePreferences', JSON.stringify(allEnabled));
    localStorage.setItem('cookiesConsent', 'all');
    onAccept();
  };

  const toggleCategory = (category: keyof CookiePreferences) => {
    if (category === 'necessary') return; // Necessary cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <CookieBanner
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <CookieContent>
            <CookieText>
              <FiInfo style={{ marginRight: '0.5rem', color: '#8b5cf6' }} />
              We use cookies to enhance your experience, analyze site traffic, and personalize content. 
              By continuing to use this site, you consent to our use of cookies. 
              Learn more in our <CookieLink href="/privacy-policy">Privacy Policy</CookieLink>.
            </CookieText>
            <CookieButtons>
              <CookieButton
                variant="settings"
                onClick={() => setShowSettings(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiSettings size={16} />
                Settings
              </CookieButton>
              <CookieButton
                variant="secondary"
                onClick={onDecline}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Decline
              </CookieButton>
              <CookieButton
                variant="primary"
                onClick={handleAcceptAll}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiCheck size={16} />
                Accept All
              </CookieButton>
            </CookieButtons>
          </CookieContent>
        </CookieBanner>
      )}

      <AnimatePresence>
        {showSettings && (
          <>
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSettings(false)}
            />
            <SettingsModal
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <SettingsHeader>
                <SettingsTitle>Cookie Preferences</SettingsTitle>
                <CloseButton onClick={() => setShowSettings(false)}>
                  <FiX />
                </CloseButton>
              </SettingsHeader>

              <CookieCategory>
                <CategoryHeader>
                  <CategoryTitle>Necessary Cookies</CategoryTitle>
                  <CategoryToggle isEnabled={true}>
                    <ToggleHandle />
                  </CategoryToggle>
                </CategoryHeader>
                <CategoryDescription>
                  These cookies are essential for the website to function properly and cannot be disabled.
                </CategoryDescription>
              </CookieCategory>

              <CookieCategory>
                <CategoryHeader>
                  <CategoryTitle>Analytics Cookies</CategoryTitle>
                  <CategoryToggle 
                    isEnabled={preferences.analytics}
                    onClick={() => toggleCategory('analytics')}
                  >
                    <ToggleHandle 
                      animate={{ x: preferences.analytics ? 26 : 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  </CategoryToggle>
                </CategoryHeader>
                <CategoryDescription>
                  Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                </CategoryDescription>
              </CookieCategory>

              <CookieCategory>
                <CategoryHeader>
                  <CategoryTitle>Marketing Cookies</CategoryTitle>
                  <CategoryToggle 
                    isEnabled={preferences.marketing}
                    onClick={() => toggleCategory('marketing')}
                  >
                    <ToggleHandle 
                      animate={{ x: preferences.marketing ? 26 : 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  </CategoryToggle>
                </CategoryHeader>
                <CategoryDescription>
                  Used to track visitors across websites to display relevant and engaging advertisements.
                </CategoryDescription>
              </CookieCategory>

              <CookieCategory>
                <CategoryHeader>
                  <CategoryTitle>Preference Cookies</CategoryTitle>
                  <CategoryToggle 
                    isEnabled={preferences.preferences}
                    onClick={() => toggleCategory('preferences')}
                  >
                    <ToggleHandle 
                      animate={{ x: preferences.preferences ? 26 : 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  </CategoryToggle>
                </CategoryHeader>
                <CategoryDescription>
                  Allow the website to remember information that changes the way it behaves or looks.
                </CategoryDescription>
              </CookieCategory>

              <CookieButtons style={{ marginTop: '2rem' }}>
                <CookieButton
                  variant="secondary"
                  onClick={() => setShowSettings(false)}
                  style={{ flex: 1 }}
                >
                  Cancel
                </CookieButton>
                <CookieButton
                  variant="primary"
                  onClick={handleSavePreferences}
                  style={{ flex: 1 }}
                >
                  Save Preferences
                </CookieButton>
              </CookieButtons>
            </SettingsModal>
          </>
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
};

export default CookieConsent; 